import { useSyncExternalStore } from "react";

export type MinimapPreference = "disabled" | "auto" | "always";

const STORAGE_KEY = "drawOla-minimap-pref";
const DEFAULT_PREFERENCE: MinimapPreference = "auto";

const isValidPreference = (value: unknown): value is MinimapPreference =>
  value === "disabled" || value === "auto" || value === "always";

const readFromStorage = (): MinimapPreference => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isValidPreference(raw)) {
      return raw;
    }
  } catch {
    // localStorage may be blocked (private mode, etc.); fall back to default.
  }
  return DEFAULT_PREFERENCE;
};

// Module-level shared store. Multiple components calling useMinimapPreference
// in the same tab subscribe to this store, so a setPreference() call from one
// component (e.g. the menu item) immediately notifies the others (e.g. the
// Minimap visibility check). The browser "storage" event only fires in OTHER
// tabs, so we need this in-tab fan-out separately.
let cachedPreference: MinimapPreference = readFromStorage();
const listeners = new Set<() => void>();

const notify = () => {
  for (const listener of listeners) {
    listener();
  }
};

const setPreference = (next: MinimapPreference) => {
  if (cachedPreference === next) {
    return;
  }
  cachedPreference = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Ignore write failures; the in-memory state still works for the session.
  }
  notify();
};

if (typeof window !== "undefined") {
  // Cross-tab sync: another tab edited the preference, mirror it locally.
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) {
      return;
    }
    const next: MinimapPreference =
      e.newValue === null
        ? DEFAULT_PREFERENCE
        : isValidPreference(e.newValue)
        ? e.newValue
        : DEFAULT_PREFERENCE;
    if (cachedPreference !== next) {
      cachedPreference = next;
      notify();
    }
  });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => cachedPreference;

export const useMinimapPreference = (): [
  MinimapPreference,
  (next: MinimapPreference) => void,
] => {
  const preference = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return [preference, setPreference];
};
